// Device picker for the flow inspector: a floating window that walks farm → device, the
// same order the farms section itself uses. Floating, not modal: the editor stays usable
// underneath — a run bar to read, a step to look at — while a phone is being chosen.
//
// Why a picker and not one long <select>: the panel reaches phones through four different
// sources — host/aggregator farms publish them on the live device map, duo and geelark are
// synced per farm from the provider's API — so a single flat list could only ever show the
// live half of them (which is why duo/geelark phones were missing). Picking the farm first
// tells us WHICH source to ask.
//
// Every kind can be inspected: the ui-dump route runs uiautomator through the host runner
// for duo/geelark/aggregator phones and over the relay for host farms.
import { esc } from '../ui/modal';
import { createFloatWindow } from '../ui/float-window';
import { t } from '../ui/lang';
import { appState, deviceAliases, loadDeviceNames } from '../core/state';
import { FarmKind, isDuoFarm, isGeelarkFarm, normalizeFarmKind } from '../core/farm-kind';
import { farmDevices } from '../farms/farms-view';
import { fetchDuoDevices } from '../farms/duo-devices';
import { fetchGeelarkDevices } from '../farms/geelark-devices';
const KIND_LABEL = {
    [FarmKind.Host]: 'nav.farm_regular',
    [FarmKind.Aggregator]: 'nav.farm_aggregator',
    [FarmKind.Duo]: 'nav.farm_duo',
    [FarmKind.Geelark]: 'nav.farm_geelark',
    [FarmKind.Adspower]: 'nav.farm_adspower',
    [FarmKind.MercadoApi]: 'nav.farm_mercado_api',
};
/** The phones of one farm, from whichever source that farm's kind publishes them on. */
async function devicesOfFarm(farm) {
    const kind = normalizeFarmKind(farm.farm_kind);
    if (isDuoFarm(kind)) {
        const { devices } = await fetchDuoDevices(farm.id);
        return devices.map((d) => ({ udid: d.udid, name: d.name || d.udid, online: d.online }));
    }
    if (isGeelarkFarm(kind)) {
        const { devices } = await fetchGeelarkDevices(farm.id);
        return devices.map((d) => ({ udid: d.udid, name: d.name || d.udid, online: d.online }));
    }
    // Same name the device card shows: the custom alias first, the model only as a
    // fallback — a farm of identical phones is otherwise seven rows of "moto g06".
    return farmDevices(appState.allDevices, farm.id, farm.name).map((d) => ({
        udid: d.udid,
        name: deviceAliases.get(d.udid) || d['ro.product.model'] || d.udid,
        online: d.state === 'device',
    }));
}
/** Open the picker. `onPick` fires once, with the chosen phone; the window closes itself.
 *  `anchor` is the page the picker belongs to — leaving it takes the picker down too. */
export function openFlowDevicePicker(onPick, anchor) {
    const win = createFloatWindow({
        title: t('pf.pick_device'), className: 'fw--fdp', ...(anchor ? { anchor } : {}),
        onClose: () => win.destroy(),
    });
    win.body.innerHTML = `
        <div class="fdp">
            <div class="fdp__crumbs"></div>
            <div class="fdp__list"><p class="settings-hint">${esc(t('common.loading'))}</p></div>
            <div class="settings-error modal__error fdp__error" style="display:none"></div>
        </div>
    `;
    const crumbsEl = win.body.querySelector('.fdp__crumbs');
    const listEl = win.body.querySelector('.fdp__list');
    const errEl = win.body.querySelector('.fdp__error');
    win.open();
    const showErr = (m) => { errEl.textContent = m; errEl.style.display = 'block'; };
    let farms = [];
    function renderFarms() {
        errEl.style.display = 'none';
        crumbsEl.innerHTML = `<span class="fdp__crumb fdp__crumb--here">${esc(t('nav.farms'))}</span>`;
        listEl.innerHTML = farms.length
            ? farms.map((f) => `
                <button type="button" class="fdp__row" data-farm="${f.id}">
                    <span class="fdp__name">${esc(f.name)}</span>
                    <span class="fdp__kind">${esc(t(KIND_LABEL[normalizeFarmKind(f.farm_kind)]))}</span>
                    <span class="fdp__chev">›</span>
                </button>`).join('')
            : `<p class="settings-hint">${esc(t('pf.no_farms'))}</p>`;
        listEl.querySelectorAll('[data-farm]').forEach((b) => {
            b.addEventListener('click', () => {
                const farm = farms.find((f) => f.id === Number(b.dataset['farm']));
                if (farm)
                    void openFarm(farm);
            });
        });
    }
    async function openFarm(farm) {
        errEl.style.display = 'none';
        crumbsEl.innerHTML = `
            <button type="button" class="fdp__crumb fdp__back">${esc(t('nav.farms'))}</button>
            <span class="fdp__sep">›</span>
            <span class="fdp__crumb fdp__crumb--here">${esc(farm.name)}</span>`;
        crumbsEl.querySelector('.fdp__back').addEventListener('click', renderFarms);
        listEl.innerHTML = `<p class="settings-hint">${esc(t('common.loading'))}</p>`;
        let devices = [];
        try {
            devices = await devicesOfFarm(farm);
        }
        catch (e) {
            showErr(e.message);
        }
        // Offline phones stay listed but unclickable: a dump needs a live adb session, and
        // hiding them would just read as "the farm is empty".
        devices.sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name));
        listEl.innerHTML = devices.length
            ? devices.map((d) => `
                <button type="button" class="fdp__row" data-udid="${esc(d.udid)}"${d.online ? '' : ' disabled'}>
                    <span class="fdp__dot${d.online ? ' fdp__dot--on' : ''}"></span>
                    <span class="fdp__name">${esc(d.name)}</span>
                    <span class="fdp__udid">${esc(d.udid)}</span>
                </button>`).join('')
            : `<p class="settings-hint">${esc(t('pf.no_devices'))}</p>`;
        listEl.querySelectorAll('[data-udid]').forEach((b) => {
            b.addEventListener('click', () => {
                const udid = b.dataset['udid'];
                const name = devices.find((d) => d.udid === udid)?.name ?? udid;
                win.destroy();
                onPick({ udid, label: `${farm.name} · ${name}`, farmId: farm.id, kind: normalizeFarmKind(farm.farm_kind) });
            });
        });
    }
    void (async () => {
        try {
            // The aliases are loaded once at startup; refill them if this page came up
            // before that finished, so the list never falls back to bare model names.
            if (!deviceAliases.size)
                await loadDeviceNames();
            const res = await fetch('/api/farms');
            if (!res.ok)
                throw new Error(await res.text());
            farms = (await res.json())
                .sort((a, b) => a.name.localeCompare(b.name));
            renderFarms();
        }
        catch (e) {
            listEl.innerHTML = '';
            showErr(e.message);
        }
    })();
}
